import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchFusionInspection } from '../../../services/api';
import { useTranslation } from '../../../context/LanguageContext';
import SyncSourceNote from './SyncSourceNote';
import { SHOW_DATA_SOURCE } from '../../../config/features';
import './FusionInspectionNew.css';

/* ===== 품질·생산 > 융합검사 =====
 * 공정검사(공정 AI 점수)와 비전검사(비전 불량 확률)를 Fusion Decision Engine 이 합친 최종 판정을 보여준다.
 *   S_fusion = α·S_vision + β·S_process  (α+β=1),  S_fusion ≥ θ → 불량
 * (α, β, θ) 는 불량 유형별 룩업 테이블(활성 버전)에서 고른다. 판정은 AI 서비스가 저장하고 이 화면은 읽기만 한다.
 *
 * 데이터: /machine/fusion-inspection/{id}?date=&verdict=&case=&page=&size=
 * 스타일은 fu- 접두어만 쓴다. */

const PAGE_SIZE = 15;
const REFRESH_MS = 30000;
const MATRIX = [
  // [키, 공정 NG?, 비전 NG?]
  ['POK_VOK', false, false],
  ['POK_VNG', false, true],
  ['PNG_VOK', true, false],
  ['PNG_VNG', true, true]
];
const CASE_FILTERS = ['', 'disagree', 'changed', 'rescued', 'suppressed'];
const FINAL_TYPES = ['FLASH', 'WELDLINE', 'SHORT', 'SINK', 'PROCESS'];
const VISION_IMAGE_VERSION = 2;   // VisionInspectionNew 와 같은 값 (nginx 1년 캐시 우회)

const pad2 = (n) => String(n).padStart(2, '0');
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const timeOf = (iso) => {
  const m = iso ? String(iso).match(/T(\d{2}:\d{2}:\d{2})/) : null;
  return m ? m[1] : '-';
};
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}%`);
const imageSrc = (file) => (file ? `/vision/${file}?v=${VISION_IMAGE_VERSION}` : null);

/* 점수 막대 (0~1). theta 가 있으면 임계선 표시 */
function ScoreBar({ value, theta, tone }) {
  const v = value == null ? 0 : Math.max(0, Math.min(1, Number(value)));
  return (
    <span className="fu-bar">
      <span className={`fu-bar-fill fu-tone-${tone}`} style={{ width: `${v * 100}%` }} />
      {theta != null && <span className="fu-bar-theta" style={{ left: `${Number(theta) * 100}%` }} />}
    </span>
  );
}

function Verdict({ v, tr }) {
  if (v !== 'OK' && v !== 'NG') return <span className="fu-muted">—</span>;
  return <span className={`fu-badge ${v === 'NG' ? 'fu-badge-ng' : 'fu-badge-ok'}`}>{tr(v === 'NG' ? 'ng' : 'ok')}</span>;
}

function FusionInspectionNew({ equipmentId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.fusionInspection.${key}`, params), [t]);
  const typeLabel = useCallback((type) => tr(`types.${type || 'DEFAULT'}`), [tr]);

  const [date, setDate] = useState(todayStr());
  const [verdict, setVerdict] = useState('');
  const [caseFilter, setCaseFilter] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setDate(todayStr());
    setVerdict('');
    setCaseFilter('');
    setPage(0);
    setSelected(null);
  }, [equipmentId]);

  const load = useCallback(async (silent = false) => {
    if (!equipmentId) return;
    if (!silent) setIsLoading(true);
    try {
      const res = await fetchFusionInspection(equipmentId, { date, verdict, caseFilter, page, size: PAGE_SIZE });
      setData(res || null);
      setError(null);
    } catch (e) {
      console.error('Failed to load fusion inspection:', e);
      if (!silent) setError(t('pages.monitoring.machine.common.dataLoadFailed'));
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [equipmentId, date, verdict, caseFilter, page, t]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (date !== todayStr()) return undefined;
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [date, load]);

  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const s = data?.summary;
  const items = data?.items || [];
  const totalItems = data?.totalItems || 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const weights = data?.weights || [];
  const isPlanWeights = weights.length > 0 && weights.every((w) => w.source !== 'grid-search');

  const dateOptions = useMemo(() => {
    const set = new Set(data?.availableDates || []);
    set.add(todayStr());
    set.add(date);
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [data, date]);

  const typeMax = Math.max(1, ...FINAL_TYPES.map((k) => Number(s?.byFinalType?.[k]) || 0));

  const selectCase = (c) => { setCaseFilter(caseFilter === c ? '' : c); setPage(0); };

  const kpis = [
    { key: 'decided', value: (s?.decided ?? 0).toLocaleString(), tone: 'neutral' },
    { key: 'processRate', value: pct(s?.processNgRate), sub: tr('countN', { n: (s?.processNg ?? 0).toLocaleString() }), tone: 'process' },
    { key: 'visionRate', value: pct(s?.visionNgRate), sub: tr('countN', { n: (s?.visionNg ?? 0).toLocaleString() }), tone: 'vision' },
    { key: 'fusionRate', value: pct(s?.fusionNgRate), sub: tr('countN', { n: (s?.fusionNg ?? 0).toLocaleString() }), tone: 'fusion' },
    { key: 'rescued', value: (s?.rescued ?? 0).toLocaleString(), tone: 'fusion', filter: 'rescued' },
    { key: 'suppressed', value: (s?.suppressed ?? 0).toLocaleString(), tone: 'muted', filter: 'suppressed' }
  ];

  return (
    <div className="fu-root">
      {SHOW_DATA_SOURCE && <SyncSourceNote equipmentId={equipmentId} />}
      {/* 화면 맞춤(2026-10-28): KPI → [판정식·가중치 | 2×2 일치표 | 융합 불량 유형] 한 줄 → 필터 → 표(남은 높이, 안에서 스크롤) */}
      {/* KPI */}
      <div className="fu-kpis">
        {kpis.map((k) => (
          <button
            key={k.key}
            type="button"
            className={`fu-kpi fu-kpi-${k.tone} ${k.filter ? 'fu-kpi-click' : ''} ${k.filter && caseFilter === k.filter ? 'active' : ''}`}
            onClick={k.filter ? () => selectCase(k.filter) : undefined}
            disabled={!k.filter}
            title={k.filter ? tr(`kpiHint.${k.key}`) : undefined}
          >
            <span className="fu-kpi-label">{tr(`kpi.${k.key}`)}</span>
            <span className="fu-kpi-value">{k.value}</span>
            {k.sub && <span className="fu-kpi-sub">{k.sub}</span>}
          </button>
        ))}
      </div>

      <div className="fu-top">
        {/* 판정식 + 유형별 가중치 테이블 */}
        <section className="fu-panel fu-weights">
          <div className="fu-panel-head">
            <span className="fu-panel-title">{tr('weightsTitle')}</span>
            <span className={`fu-version ${isPlanWeights ? 'fu-version-plan' : ''}`} title={isPlanWeights ? tr('planWeightsNote') : undefined}>
              {data?.weightVersion || '—'}{isPlanWeights ? ` · ${tr('planWeights')}` : ''}
            </span>
          </div>
          <div className="fu-formula-line" title={`${tr('legendVision')}\n${tr('legendProcess')}\n${tr('legendWeights')}`}>
            <span className="fu-formula-eq">
              S<sub>fusion</sub> = <b className="fu-c-vision">α</b>·S<sub>vision</sub> + <b className="fu-c-process">β</b>·S<sub>process</sub>
            </span>
            <span className="fu-formula-rule">{tr('formulaRule')}</span>
          </div>
          <table className="fu-wtable">
            <thead>
              <tr><th>{tr('colType')}</th><th>α {tr('vision')}</th><th>β {tr('process')}</th><th>θ</th><th>{tr('colSplit')}</th></tr>
            </thead>
            <tbody>
              {weights.length === 0 && <tr><td colSpan={5} className="fu-muted">{tr('noWeights')}</td></tr>}
              {weights.map((w) => (
                <tr key={w.defectType}>
                  <td>{typeLabel(w.defectType)}</td>
                  <td className="fu-c-vision">{num(w.alpha)}</td>
                  <td className="fu-c-process">{num(w.beta)}</td>
                  <td>{num(w.theta)}</td>
                  <td>
                    <span className="fu-split">
                      <span className="fu-split-v" style={{ width: `${Number(w.alpha) * 100}%` }} />
                      <span className="fu-split-p" style={{ width: `${Number(w.beta) * 100}%` }} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* '계획서 초기값' 안내 전문은 버전 배지 툴팁(title)에 둔다 — 화면 맞춤으로 표 높이를 확보 */}
        </section>

        {/* 2×2 일치표 */}
        <section className="fu-panel">
          <div className="fu-panel-head">
            <span className="fu-panel-title">{tr('matrixTitle')}</span>
            <span className="fu-panel-sub">{tr('matrixSub')}</span>
          </div>
          <div className="fu-matrix">
            <div />
            <div className="fu-mx-head">{tr('visionOk')}</div>
            <div className="fu-mx-head">{tr('visionNg')}</div>
            {[false, true].map((pNg) => (
              <React.Fragment key={String(pNg)}>
                <div className="fu-mx-head fu-mx-row">{tr(pNg ? 'processNg' : 'processOk')}</div>
                {MATRIX.filter(([, p]) => p === pNg).map(([key, p, v]) => {
                  const cell = s?.matrix?.[key] || { total: 0, fusionNg: 0 };
                  const disagree = p !== v;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`fu-mx-cell ${disagree ? 'fu-mx-disagree' : ''} ${p && v ? 'fu-mx-both' : ''} ${caseFilter === key ? 'active' : ''}`}
                      onClick={() => selectCase(key)}
                    >
                      <span className="fu-mx-total">{Number(cell.total).toLocaleString()}</span>
                      <span className="fu-mx-fused">{tr('fusedNg', { n: Number(cell.fusionNg).toLocaleString() })}</span>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="fu-panel">
          <div className="fu-panel-head">
            <span className="fu-panel-title">{tr('finalTypeTitle')}</span>
            <span className="fu-panel-sub">{tr('countN', { n: (s?.fusionNg ?? 0).toLocaleString() })}</span>
          </div>
          {FINAL_TYPES.map((k) => {
            const n = Number(s?.byFinalType?.[k]) || 0;
            return (
              <div key={k} className="fu-type-row">
                <span className={`fu-type-dot fu-type-${k}`} />
                <span className="fu-type-name">{typeLabel(k)}</span>
                <span className="fu-type-bar"><span className={`fu-type-fill fu-type-${k}`} style={{ width: `${(n / typeMax) * 100}%` }} /></span>
                <span className="fu-type-count">{n.toLocaleString()}</span>
              </div>
            );
          })}
          <div className="fu-note">{tr('processTypeNote')}</div>
        </section>
      </div>

      {/* 필터 */}
      <div className="fu-filter-row">
        <span className="fu-filter-label">{tr('date')}</span>
        <div className="fu-select-wrap">
          <select className="fu-select" value={date} onChange={(e) => { setDate(e.target.value); setPage(0); }}>
            {dateOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <span className="material-symbols-outlined fu-select-icon">expand_more</span>
        </div>
        <span className="fu-filter-label">{tr('finalVerdict')}</span>
        <div className="fu-seg">
          {[['', 'all'], ['OK', 'ok'], ['NG', 'ng']].map(([v, key]) => (
            <button key={key} type="button" className={`fu-seg-btn ${verdict === v ? 'active' : ''}`} onClick={() => { setVerdict(v); setPage(0); }}>
              {tr(key)}
            </button>
          ))}
        </div>
        <span className="fu-filter-label">{tr('caseLabel')}</span>
        <div className="fu-chips">
          {CASE_FILTERS.map((c) => (
            <button key={c || 'all'} type="button" className={`fu-chip ${caseFilter === c ? 'active' : ''}`} onClick={() => { setCaseFilter(c); setPage(0); }}>
              {tr(`cases.${c || 'all'}`)}
            </button>
          ))}
          {MATRIX.some(([k]) => k === caseFilter) && (
            <button type="button" className="fu-chip active" onClick={() => { setCaseFilter(''); setPage(0); }}>
              {tr(`cells.${caseFilter}`)} ✕
            </button>
          )}
        </div>
        <span className="fu-result-count">{tr('countN', { n: totalItems.toLocaleString() })}</span>
      </div>

      {/* 목록 */}
      {isLoading && !data ? (
        <div className="fu-message">{t('pages.monitoring.machine.common.loading')}</div>
      ) : error ? (
        <div className="fu-message fu-message-error">{error}</div>
      ) : items.length === 0 ? (
        <div className="fu-message">{s?.decided ? tr('emptyFiltered') : tr('empty')}</div>
      ) : (
        <div className="fu-table-wrap">
          <table className="fu-table">
            <thead>
              <tr>
                <th>{tr('colTime')}</th>
                <th>{tr('colShot')}</th>
                <th>{tr('process')}</th>
                <th>{tr('vision')}</th>
                <th>{tr('colWeight')}</th>
                <th className="fu-col-score">{tr('colFusion')}</th>
                <th>{tr('finalVerdict')}</th>
                <th>{tr('colCase')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.shotUuid} className={it.fusionVerdict === 'NG' ? 'fu-row-ng' : ''} onClick={() => setSelected(it)}>
                  <td className="fu-num">{timeOf(it.shotTime)}</td>
                  <td className="fu-num">#{it.shotCount ?? '-'}</td>
                  <td>
                    <span className="fu-cell-score"><Verdict v={it.processVerdict} tr={tr} /><span className="fu-num">{num(it.processScore)}</span></span>
                  </td>
                  <td>
                    <span className="fu-cell-score">
                      <Verdict v={it.visionVerdict} tr={tr} />
                      <span className="fu-num">{num(it.visionScore)}</span>
                      {it.visionVerdict === 'NG' && <span className={`fu-type-tag fu-type-${it.visionDefectType || 'DEFAULT'}`}>{typeLabel(it.visionDefectType)}</span>}
                    </span>
                  </td>
                  <td className="fu-num fu-weight">
                    <span className="fu-c-vision">{num(it.alpha)}</span> / <span className="fu-c-process">{num(it.beta)}</span>
                  </td>
                  <td className="fu-col-score">
                    <span className="fu-cell-score">
                      <ScoreBar value={it.fusionScore} theta={it.theta} tone={it.fusionVerdict === 'NG' ? 'ng' : 'fusion'} />
                      <span className="fu-num">{num(it.fusionScore, 3)}</span>
                    </span>
                  </td>
                  <td><Verdict v={it.fusionVerdict} tr={tr} /></td>
                  <td><span className={`fu-case fu-case-${(it.decisionCase || '').toLowerCase()}`}>{tr(`decisionCase.${it.decisionCase || 'NONE'}`)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 아래 줄: 점수 출처 안내(왼쪽) + 페이지(가운데) */}
      {(totalItems > PAGE_SIZE || (s && s.decided > 0 && s.modelScored < s.decided)) && (
        <div className="fu-foot">
          <span className="fu-note">
            {s && s.decided > 0 && s.modelScored < s.decided
              ? tr('verdictScoreNote', { n: (s.decided - s.modelScored).toLocaleString() }) : ''}
          </span>
          {totalItems > PAGE_SIZE && (
            <div className="fu-pagination">
              <button type="button" className="fu-page-nav" disabled={page === 0} onClick={() => setPage(page - 1)}>
                <span className="material-symbols-outlined">chevron_left</span>{tr('prev')}
              </button>
              <span className="fu-page-info">{page + 1} / {totalPages}</span>
              <button type="button" className="fu-page-nav" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                {tr('next')}<span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          )}
          <span />
        </div>
      )}

      {/* 상세 */}
      {selected && (
        <div className="fu-modal-backdrop" onClick={() => setSelected(null)} role="presentation">
          <div className="fu-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="fu-modal-head">
              <span className="fu-modal-title">{tr('detailTitle', { n: selected.shotCount ?? '-' })}</span>
              <button type="button" className="fu-modal-close" onClick={() => setSelected(null)} aria-label={tr('close')}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="fu-modal-body">
              <div className="fu-modal-img">
                {imageSrc(selected.imageFile)
                  ? <img src={imageSrc(selected.imageFile)} alt={`Shot #${selected.shotCount}`} />
                  : <span className="fu-muted">{tr('noImage')}</span>}
              </div>
              <div className="fu-modal-right">
                <div className="fu-score-line">
                  <span className="fu-score-name">{tr('process')}</span>
                  <ScoreBar value={selected.processScore} tone="process" />
                  <span className="fu-num">{num(selected.processScore, 3)}</span>
                  <Verdict v={selected.processVerdict} tr={tr} />
                </div>
                <div className="fu-score-line">
                  <span className="fu-score-name">{tr('vision')}</span>
                  <ScoreBar value={selected.visionScore} tone="vision" />
                  <span className="fu-num">{num(selected.visionScore, 3)}</span>
                  <Verdict v={selected.visionVerdict} tr={tr} />
                </div>
                <div className="fu-score-line fu-score-final">
                  <span className="fu-score-name">{tr('fusion')}</span>
                  <ScoreBar value={selected.fusionScore} theta={selected.theta} tone={selected.fusionVerdict === 'NG' ? 'ng' : 'fusion'} />
                  <span className="fu-num">{num(selected.fusionScore, 3)}</span>
                  <Verdict v={selected.fusionVerdict} tr={tr} />
                </div>

                <div className="fu-calc">
                  <div className="fu-calc-title">{tr('calcTitle')}</div>
                  <div className="fu-calc-eq fu-num">
                    <span className="fu-c-vision">{num(selected.alpha)}</span> × {num(selected.visionScore, 3)}
                    {' + '}
                    <span className="fu-c-process">{num(selected.beta)}</span> × {num(selected.processScore, 3)}
                    {' = '}<b>{num(selected.fusionScore, 3)}</b>
                    {' '}{Number(selected.fusionScore) >= Number(selected.theta) ? '≥' : '<'} θ {num(selected.theta)}
                    {' → '}<b className={selected.fusionVerdict === 'NG' ? 'fu-c-ng' : 'fu-c-ok'}>{tr(selected.fusionVerdict === 'NG' ? 'ng' : 'ok')}</b>
                  </div>
                  <div className="fu-calc-meta">
                    {tr('appliedWeight', { type: typeLabel((selected.weightVersion || '').split('/')[1] || 'DEFAULT') })}
                    {' · '}{tr('margin')} {num(selected.margin)}
                  </div>
                </div>

                <dl className="fu-info">
                  <dt>{tr('decisionCaseLabel')}</dt><dd>{tr(`decisionCase.${selected.decisionCase || 'NONE'}`)}</dd>
                  <dt>{tr('finalType')}</dt><dd>{selected.fusionVerdict === 'NG' ? typeLabel(selected.finalDefectType) : '—'}</dd>
                  <dt>{tr('shotTime')}</dt><dd className="fu-num">{selected.shotTime ? String(selected.shotTime).replace('T', ' ').slice(0, 19) : '-'}</dd>
                  <dt>{tr('mold')}</dt><dd>{selected.craftId || '-'}</dd>
                  <dt>{tr('scoreSource')}</dt><dd>{tr(`source.${selected.scoreSource || 'verdict'}`)}</dd>
                  <dt>{tr('weightVersion')}</dt><dd className="fu-mono">{selected.weightVersion || '-'}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FusionInspectionNew;
